'use client'

import type {
  LoaderProps,
  UseImageProps,
  UseImageReturnType,
} from '@maestro/commerce-blocks/components/image-product/image-product.types'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'

import { buildImageUrl } from '@maestro/commerce-blocks/components/image-product/utils/build-image-url'
import {
  buildSizes,
  buildSrcSet,
} from '@maestro/commerce-blocks/utils/image.utils'
import { theme } from '@maestro/local/theme'

export const useImageProduct = (props: UseImageProps): UseImageReturnType => {
  const {
    src,
    width,
    srcSetConfig,
    sizesConfig,
    fit,
    forceIsLoaded,
    backgroundColor,
    canvas,
    enableLazyLoading,
    quality = theme().images.quality,
    base64Blur,
    cachedUrl,
    fetchPriority = 'auto',
    showPlaceholder = true,
    enableFadeIn = true,
  } = props

  const [isLoaded, setIsLoaded] = useState(false)

  useLayoutEffect(() => {
    // Reset load state before paint when src changes to avoid briefly showing
    // the previously rendered image while the next source is loading.
    setIsLoaded(false)
  }, [src])

  const generateUrl = useCallback((data: LoaderProps) => {
    return theme().images.localAssetImages.includes(data.src)
      ? data.src
      : buildImageUrl({
          src: data.src,
          width: data.width,
          height: data.height,
          quality: data.quality,
          canvas: data.canvas,
          backgroundColor: data.backgroundColor,
          fit: data.fit,
        })
  }, [])

  const preloadSrc = useMemo(() => {
    return generateUrl({
      src,
      width: theme().images.thumbnailWidth,
      height: theme().images.thumbnailHeight,
      fit,
    })
  }, [fit, generateUrl, src])

  const imageSrc = useMemo(() => {
    return generateUrl({
      src,
      width,
      quality,
      canvas,
      backgroundColor,
      fit,
    })
  }, [backgroundColor, canvas, fit, generateUrl, quality, src, width])

  useEffect(() => {
    if (enableLazyLoading && typeof window !== 'undefined') {
      const loaded = sessionStorage.getItem(`image-loaded-${src}`) === 'true'
      setIsLoaded(loaded)
    }
  }, [enableLazyLoading, src])

  useEffect(() => {
    if (isLoaded && enableLazyLoading && typeof window !== 'undefined') {
      sessionStorage.setItem(`image-loaded-${src}`, 'true')
    }
  }, [enableLazyLoading, isLoaded, src])

  // If the image is already in the cache and
  // React doesn't mount the <img> from scratch, onLoad may not be called,
  // we have to do it manually. Using decode() instead of complete/onload
  // to ensure the image is fully decoded and ready to paint before swapping,
  // which prevents a flash of empty space on Firefox.
  useEffect(() => {
    if (typeof window === 'undefined' || enableLazyLoading || !src) {
      return
    }

    const img = new window.Image()
    img.src = imageSrc
    img.decode().finally(() => setIsLoaded(true))
  }, [enableLazyLoading, src, imageSrc])

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    skip: !enableLazyLoading || isLoaded,
  })

  const sizes = useMemo(() => buildSizes(sizesConfig), [sizesConfig])

  const srcSet = useMemo(() => {
    return buildSrcSet(srcSetConfig, (width, height) =>
      generateUrl({ src, width, height, quality, canvas, backgroundColor, fit })
    )
  }, [backgroundColor, canvas, fit, generateUrl, quality, src, srcSetConfig])

  const finalIsLoaded = useMemo(() => {
    return forceIsLoaded ?? isLoaded
  }, [forceIsLoaded, isLoaded])

  const showImage = useMemo(() => {
    return !enableLazyLoading || inView || finalIsLoaded
  }, [enableLazyLoading, inView, finalIsLoaded])

  const placeholderVisibility = useMemo(() => {
    const hasPlaceholderData = !!base64Blur || !!cachedUrl

    // For LCP (fetchPriority="high"), only show placeholder if we have instant data
    const canShowPlaceholder =
      showPlaceholder && (fetchPriority !== 'high' || hasPlaceholderData)

    // Hide final image while loading when we have something to show underneath
    const shouldHideFinalImage =
      !finalIsLoaded &&
      (enableFadeIn || hasPlaceholderData) &&
      (fetchPriority !== 'high' || hasPlaceholderData)

    return {
      canShowPlaceholder,
      shouldHideFinalImage,
    }
  }, [
    base64Blur,
    cachedUrl,
    enableFadeIn,
    fetchPriority,
    finalIsLoaded,
    showPlaceholder,
  ])

  return {
    finalIsLoaded,
    imageSrc,
    inView,
    isLoaded,
    preloadSrc,
    ref,
    setIsLoaded,
    sizes,
    srcSet,
    showImage,
    ...placeholderVisibility,
  }
}
